import { animaliaListingDesignTokens } from '../shared/animalia-listing-design-tokens';

export interface AnimaliaListingSectionHeadState {
  mode: 'listing' | 'search';
  draftQuery: string;
  appliedQuery: string;
  pendingQuery: string;
  resultCount: number;
  isLoading: boolean;
}

export class MvAnimaliaListingSectionHead extends HTMLElement {
  private state: AnimaliaListingSectionHeadState = {
    mode: 'listing',
    draftQuery: '',
    appliedQuery: '',
    pendingQuery: '',
    resultCount: 0,
    isLoading: false,
  };

  private readonly handleInput = (event: Event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    this.updateClearButtonVisibility(target.value.trim() !== '' || this.state.appliedQuery.trim() !== '');

    this.dispatchEvent(
      new CustomEvent<{ value: string }>('search-query-changed', {
        bubbles: true,
        composed: true,
        detail: {
          value: target.value,
        },
      }),
    );
  };

  private readonly handleSubmit = (event: Event) => {
    event.preventDefault();

    const input = this.shadowRoot?.querySelector<HTMLInputElement>('[data-search-input]');

    this.dispatchEvent(
      new CustomEvent<{ value: string }>('search-submitted', {
        bubbles: true,
        composed: true,
        detail: {
          value: input?.value ?? this.state.draftQuery,
        },
      }),
    );
  };

  private readonly handleClear = () => {
    this.dispatchEvent(
      new CustomEvent('search-cleared', {
        bubbles: true,
        composed: true,
      }),
    );
  };

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  setState(state: AnimaliaListingSectionHeadState): void {
    this.state = {
      ...state,
    };
    this.render();
    this.addEventListeners();
  }

  focusSearchInput(): void {
    const input = this.shadowRoot?.querySelector<HTMLInputElement>('[data-search-input]');

    if (!input) {
      return;
    }

    input.focus();

    const cursorPosition = input.value.length;

    input.setSelectionRange(cursorPosition, cursorPosition);
  }

  connectedCallback(): void {
    this.render();
    this.addEventListeners();
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }

  private render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const canClear = this.state.draftQuery.trim() !== '' || this.state.appliedQuery.trim() !== '';

    this.shadowRoot.innerHTML = `
      <style>
        ${animaliaListingDesignTokens}

        .section-head {
          display: grid;
          gap: 20px;
          margin: 28px 0 18px;
        }

        h2 {
          margin: 8px 0 0;
          color: #31463d;
          font-size: clamp(1.8rem, 2.6vw, 2.8rem);
          font-family: Georgia, 'Times New Roman', serif;
          line-height: 1.02;
        }

        .search-form {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 10px;
          padding: 14px;
          border-radius: 24px;
          background: rgba(250, 247, 241, 0.92);
          border: 1px solid rgba(49, 70, 61, 0.12);
          box-shadow: 0 14px 30px rgba(31, 42, 35, 0.08);
        }

        .search-field {
          position: relative;
          display: flex;
          align-items: center;
          min-width: 0;
        }

        .search-input {
          width: 100%;
          min-height: 50px;
          padding: 0 44px 0 16px;
          border-radius: 16px;
          border: 1px solid rgba(49, 70, 61, 0.16);
          background: rgba(255, 255, 255, 0.88);
          color: #31463d;
          font: inherit;
          appearance: none;
          -webkit-appearance: none;
        }

        .search-input::-webkit-search-cancel-button,
        .search-input::-webkit-search-decoration,
        .search-input::-webkit-search-results-button,
        .search-input::-webkit-search-results-decoration {
          -webkit-appearance: none;
          appearance: none;
        }

        .search-input:focus-visible {
          outline: 2px solid rgba(49, 70, 61, 0.32);
          outline-offset: 2px;
        }

        .clear-inline {
          position: absolute;
          right: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          padding: 0;
          border: 1px solid rgba(49, 70, 61, 0.14);
          border-radius: 999px;
          background: rgba(244, 239, 231, 0.98);
          color: #31463d;
          cursor: pointer;
          box-shadow: 0 8px 18px rgba(31, 42, 35, 0.08);
          font-size: 1.1rem;
          font-weight: 600;
          line-height: 1;
          transform: translateZ(0);
        }

        .clear-inline:hover,
        .clear-inline:focus-visible {
          background: #31463d;
          border-color: #31463d;
          color: #f7f3ed;
          outline: none;
        }

        .search-action {
          min-height: 50px;
          padding: 0 16px;
          border-radius: 999px;
          border: 1px solid #31463d;
          font: inherit;
        }

        .search-action {
          background: #31463d;
          color: #f7f3ed;
          cursor: pointer;
        }

        .search-meta {
          margin: 0;
          color: #5a6d62;
          font-size: 0.88rem;
          line-height: 1.5;
        }

        @media (max-width: 720px) {
          .search-form {
            grid-template-columns: 1fr;
          }

          .search-action {
            width: 100%;
          }
        }
      </style>

      <section class="section-head">
        <div>
          <p class="section-kicker">${this.state.mode === 'search' ? 'Busqueda activa' : 'Listado'}</p>
          <h2>${this.state.mode === 'search' ? 'Resultados' : 'Fichas'}</h2>
        </div>
        <form class="search-form" role="search" aria-label="Buscar especies">
          <div class="search-field">
            <input
              class="search-input"
              type="text"
              name="search"
              data-search-input
              placeholder="Busca por nombre cientifico o comun"
              value="${this.escapeAttribute(this.state.draftQuery)}"
              autocomplete="off"
              spellcheck="false"
            >
            <button class="clear-inline" type="button" data-search-clear aria-label="Limpiar busqueda" ${canClear ? '' : 'hidden'}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <button class="search-action" type="submit" data-search-submit>
            ${this.state.isLoading && this.state.mode === 'search' ? 'Buscando' : 'Buscar'}
          </button>
        </form>
        ${this.renderMeta()}
      </section>
    `;
  }

  private renderMeta(): string {
    const pendingDraft = this.state.draftQuery.trim();
    const appliedQuery = this.state.appliedQuery.trim();
    const pendingQuery = this.state.pendingQuery.trim();

    if (this.state.isLoading && pendingQuery !== '') {
      return `<p class="search-meta">Buscando coincidencias para &quot;${this.escapeHtml(pendingQuery)}&quot;.</p>`;
    }

    if (this.state.mode === 'search' && appliedQuery !== '') {
      const suffix = this.state.resultCount === 1 ? 'resultado' : 'resultados';

      if (pendingDraft !== '' && pendingDraft !== appliedQuery) {
        return `<p class="search-meta">Mostrando ${this.state.resultCount} ${suffix} para &quot;${this.escapeHtml(appliedQuery)}&quot;. Has editado el texto: pulsa Enter o la lupa para actualizar.</p>`;
      }

      return `<p class="search-meta">Mostrando ${this.state.resultCount} ${suffix} para &quot;${this.escapeHtml(appliedQuery)}&quot;.</p>`;
    }

    return '';
  }

  private addEventListeners(): void {
    this.shadowRoot?.querySelector('[data-search-input]')?.removeEventListener('input', this.handleInput);
    this.shadowRoot?.querySelector('form')?.removeEventListener('submit', this.handleSubmit);
    this.shadowRoot?.querySelector('[data-search-clear]')?.removeEventListener('click', this.handleClear);

    this.shadowRoot?.querySelector('[data-search-input]')?.addEventListener('input', this.handleInput);
    this.shadowRoot?.querySelector('form')?.addEventListener('submit', this.handleSubmit);
    this.shadowRoot?.querySelector('[data-search-clear]')?.addEventListener('click', this.handleClear);
  }

  private removeEventListeners(): void {
    this.shadowRoot?.querySelector('[data-search-input]')?.removeEventListener('input', this.handleInput);
    this.shadowRoot?.querySelector('form')?.removeEventListener('submit', this.handleSubmit);
    this.shadowRoot?.querySelector('[data-search-clear]')?.removeEventListener('click', this.handleClear);
  }

  private updateClearButtonVisibility(visible: boolean): void {
    const clearButton = this.shadowRoot?.querySelector<HTMLButtonElement>('[data-search-clear]');

    if (!clearButton) {
      return;
    }

    clearButton.hidden = !visible;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private escapeAttribute(value: string): string {
    return this.escapeHtml(value);
  }
}

if (!customElements.get('mv-animalia-listing-section-head')) {
  customElements.define('mv-animalia-listing-section-head', MvAnimaliaListingSectionHead);
}
